import { Dispatch, FormEvent, SetStateAction, useContext, useMemo, useState } from "react";

import { ProjectPhase } from "../../../../../../../constants/project-phase";
import { ProjectDates } from "../../../../../../../models";
import { updateProjectDetails } from "../../../../../../../service/project";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import {
  recursivelyValidateAndTransformDateToString,
  setValueFromFlattenedKey,
  useIsLoadingWrapper,
} from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";

interface UseCompletePhaseModalReturnData {
  currentPhase: ProjectPhase;
  show: boolean;
  isStartDateInFuture: boolean;
  isHandleCompletePhaseLoading: boolean;
  errors: ServiceError[] | undefined;
  handleClose: () => void;
  handleCompletePhase: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  setShowEditDatesPanel: Dispatch<SetStateAction<boolean>>;
}

const getPhaseDateKey = (
  phase: string | undefined
): "feasibility" | "design" | "implementation" | "ongoingMonitoring" | undefined => {
  switch (phase) {
    case ProjectPhase.feasibility:
      return "feasibility";
    case ProjectPhase.design:
      return "design";
    case ProjectPhase.implementation:
      return "implementation";
    case ProjectPhase.ongoingMonitoring:
      return "ongoingMonitoring";
    default:
      return undefined;
  }
};

export const useCompletePhaseModal = (): UseCompletePhaseModalReturnData => {
  const {
    currentPhase,
    projectDates,
    showCompletePhaseModal,
    setShowEditDatesPanel,
    setShowCompletePhaseModal,
    shouldRefreshTasksBoard,
    handlePhaseChange,
  } = useContext(TasksBoardContext);

  // BR-02129 - IF the user is completing the phase earlier or later than the current end date value, the user should be prompted to refactor all future phase dates using the panel editor
  const futurePhases = useMemo(() => {
    if (!projectDates) return new Set<ProjectPhase>();

    const past = new Set<ProjectPhase>();
    const today = new Date();

    if (projectDates.dates.feasibility?.start !== undefined && projectDates.dates.feasibility?.start > today) {
      past.add(ProjectPhase.feasibility);
    }
    if (projectDates.dates.design?.start !== undefined && projectDates.dates.design?.start > today) {
      past.add(ProjectPhase.design);
    }
    if (projectDates.dates.implementation?.start !== undefined && projectDates.dates.implementation?.start > today) {
      past.add(ProjectPhase.implementation);
    }
    if (
      projectDates.dates.ongoingMonitoring.overallStart !== undefined &&
      projectDates.dates.ongoingMonitoring?.overallStart > today
    ) {
      past.add(ProjectPhase.ongoingMonitoring);
    }

    return past;
  }, [projectDates]);

  const [errors, setErrors] = useState<ServiceError[] | undefined>(undefined);
  const [isHandleCompletePhaseLoading, setIsHandleCompletePhaseLoading] = useState(false);

  const handleClose = (): void => {
    setErrors([]);
    setShowCompletePhaseModal(false);
  };

  const handleCompletePhase = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (!projectDates) return;

    const orderedPhases = Object.values(ProjectPhase) as ProjectPhase[];
    const currentPhaseIndex = orderedPhases.findIndex((phase) => phase === currentPhase);
    const nextPhase = currentPhaseIndex >= 0 ? orderedPhases[currentPhaseIndex + 1] : undefined;
    const currentPhaseCamelCase = getPhaseDateKey(currentPhase);
    const nextPhaseCamelCase = getPhaseDateKey(nextPhase);

    if (!currentPhaseCamelCase) {
      setErrors([{ code: "INVALID_PROJECT_PHASE", message: "Unable to complete this workflow phase." }]);
      return;
    }

    let updatedDates = setValueFromFlattenedKey(
      `${currentPhaseCamelCase}.${currentPhase === ProjectPhase.ongoingMonitoring ? "overallStatus" : "status"}`,
      "Complete",
      projectDates?.dates
    ) as ProjectDates;

    if (nextPhaseCamelCase !== undefined) {
      updatedDates = setValueFromFlattenedKey(
        `${nextPhaseCamelCase}.${nextPhase === ProjectPhase.ongoingMonitoring ? "overallStatus" : "status"}`,
        "In progress",
        updatedDates
      ) as ProjectDates;
    }

    const transformedDates = recursivelyValidateAndTransformDateToString(updatedDates);

    if (transformedDates.errors.length > 0) {
      setErrors(transformedDates.errors);
      return;
    }

    const updateProjectDetailsRes = await updateProjectDetails({
      projectUuid: projectDates.uuid,
      dates: JSON.stringify(transformedDates.data),
      rowVersion: projectDates.rowVersion,
      addressCountryCode: null,
      displayName: null,
      locationCoordinates: null,
      locationGridReference: null,
      areaNetHa: null,
      validatorUuid: null,
      externalReference: null,
      name: null,
      addressRegion: null,
    });

    if (updateProjectDetailsRes.status === Status.Success && updateProjectDetailsRes.data) {
      Toast.success({ message: "Project phase successfully completed" });
      if (nextPhase) {
        handlePhaseChange(nextPhase as ProjectPhase);
      }
      shouldRefreshTasksBoard();
      handleClose();
    }
    if (updateProjectDetailsRes.status === Status.Error && updateProjectDetailsRes.errors) {
      setErrors(updateProjectDetailsRes.errors);
    }
  }, setIsHandleCompletePhaseLoading);

  return {
    currentPhase,
    isStartDateInFuture: futurePhases.has(currentPhase),
    show: showCompletePhaseModal,
    isHandleCompletePhaseLoading,
    errors,
    handleClose,
    handleCompletePhase,
    setShowEditDatesPanel,
  };
};
